<template>
  <!-- "Not on the page you want to read?" — the URL field, for a site the
       visitor isn't currently sitting on (and for anyone who landed here from a
       search and hasn't installed anything yet).

       It's the generator's own control, rebuilt: same ring, same favicon slot,
       same trailing button, because it does the same job. It doesn't extract
       anything itself — it hands the URL to the generator as `/?url=…`, the
       deep link the brand pages and the extension's "Generate More" both
       already use, and the homepage runs the extraction there. -->
  <div>
    <label for="extension-url" class="sr-only">Website address</label>
    <InputGroup
      id="extension-url"
      v-model="query"
      :invalid="hasError"
      type="text"
      autocomplete="off"
      spellcheck="false"
      placeholder="www.example.com"
      data-clarity-unmask="true"
      :aria-describedby="hintId"
      @keydown.enter="submit"
      @input="hasError = false"
    >
      <template #leading>
        <img :src="paletteIcon" width="24" height="24" alt="" aria-hidden="true" class="h-6 w-6" />
      </template>
      <template #trailing>
        <BaseButton variant="secondary" class="m-2 shrink-0" @click.prevent="submit">
          Extract colors
        </BaseButton>
      </template>
    </InputGroup>

    <!-- Reserved rather than conditional: the row below only appears on a bad
         input, and letting it push the examples down is a layout shift on an
         interaction that already went wrong once. -->
    <p :id="hintId" class="mt-2 min-h-5 text-sm" :class="hasError ? 'text-red-400' : 'text-gray-500'" aria-live="polite">
      {{ hasError ? 'Enter a website address, e.g. stripe.com' : 'No sign-up. The palette opens on colorize.design.' }}
    </p>

    <!-- Crawl path into the brand catalog, and a one-click demo for anyone who
         doesn't want to type: these are prerendered pages, not generator runs. -->
    <div class="mt-4 flex flex-wrap items-center gap-2">
      <span class="text-xs text-gray-500">Try:</span>
      <NuxtLink
        v-for="example in examples"
        :key="example.slug"
        :to="`/brand-colors/${example.slug}`"
        class="rounded-full border border-gray-800 bg-gray-900/40 px-3 py-1 text-xs text-gray-300 transition hover:border-gray-700 hover:bg-gray-900 hover:text-white"
        @click="onExample(example)"
      >
        {{ example.name }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
import { ref, useId } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseButton from '~/components/atoms/BaseButton.vue'
import InputGroup from '~/components/molecules/InputGroup.vue'
import paletteIcon from '~/assets/img/palettes_icon_bw.svg'

const props = defineProps({
  // Named on the event so this field's conversions stay separable from the
  // homepage's own generator.
  placement: { type: String, default: 'extension_page' },
})

const examples = [
  { slug: 'stripe', name: 'stripe.com' },
  { slug: 'airbnb', name: 'airbnb.com' },
  { slug: 'spotify', name: 'spotify.com' },
  { slug: 'notion', name: 'notion.so' },
]

const query = ref('')
const hasError = ref(false)
const hintId = useId()
const router = useRouter()
const route = useRoute()

/**
 * Trim what was typed down to something the generator can use.
 *
 * The generator does its own normalization on the other side (and the API is
 * the final judge of what resolves), so this only has to reject the two inputs
 * that would send someone to a loading spinner for nothing: an empty field, and
 * a single word with no dot in it.
 */
const normalize = (value) => {
  const trimmed = value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '')
  if (trimmed === '' || !/\./.test(trimmed.split(/[/?#]/)[0])) return ''
  return trimmed
}

const submit = () => {
  const site = normalize(query.value)
  if (!site) {
    hasError.value = true
    return
  }
  try {
    trackEvent('Extension Page URL Submitted', { site, placement: props.placement })
  } catch {}
  // Same deep link the brand pages hand over with, so the homepage picks it up
  // through the path it already has for `?url=`.
  router.push({ path: '/', query: { url: site } })
}

const onExample = (example) => {
  try {
    trackEvent('Extension Page Example Clicked', { brand: example.slug, page: route.path })
  } catch {}
}
</script>
